// P1 — Page {t("agenda.title")} avec calendrier interactif
// react-big-calendar v1+ + date-fns + locale fr
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Calendar, dateFnsLocalizer, type View } from "react-big-calendar";
import {
  format,
  parse,
  startOfWeek,
  getDay,
} from "date-fns";
import { fr } from "date-fns/locale";
import "react-big-calendar/lib/css/react-big-calendar.css";

import { useNavigate } from "react-router-dom";
import { enginsApi, agendaApi } from "@/lib/api";
import { useT } from "@/lib/i18n";

// Valeurs brutes des statuts stockés en DB (FR) — l'affichage est traduit.
type StatutEngin = "OK" | "En retard" | "Non planifié";
const STATUT_NON_PLANIFIE = "Non planifié";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ht } from "@/lib/ht";

// Setup date-fns localizer pour react-big-calendar
const locales = { fr };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date: Date) => startOfWeek(date, { weekStartsOn: 1 }), // Lundi
  getDay,
  locales,
});

interface CalendarEvent {
  id: number;
  title: string;
  start: Date;
  end: Date;
  resource: {
    engin_id: number;
    statut: StatutEngin;
    ecart_jours: number;
  };
}

const STATUT_COLORS: Record<string, string> = {
  OK: "#22c55e", // green
  "En retard": "#ef4444", // red
  "Non planifié": "#9ca3af", // gray
};

export default function CalendrierPage() {
  const t = useT();
  const [selectedEnginId, setSelectedEnginId] = useState<number | null>(null);
  const [dateCible, setDateCible] = useState<string>(
    new Date().toISOString().split("T")[0],
  );
  const [view, setView] = useState<View>("month");
  const [date, setDate] = useState(new Date());
  const navigate = useNavigate();

  // Engins
  const { data: engins } = useQuery({
    queryKey: ["engins"],
    queryFn: () => enginsApi.list(),
  });

  // Cycle préventif pour l'engin sélectionné
  const { data: cycleResult } = useQuery({
    queryKey: ["cycle", selectedEnginId, dateCible],
    queryFn: () => agendaApi.cyclePreventif(selectedEnginId!, dateCible),
    enabled: !!selectedEnginId,
  });

  // Cycle préventif pour TOUS les engins (P19c — alimente le calendrier)
  const { data: cyclesAll } = useQuery({
    queryKey: ["cycles-all"],
    queryFn: () => agendaApi.cyclePreventifAll(),
  });

  // Événements calendrier : 1 event par engin avec statut préventif (P19c)
  const events: CalendarEvent[] = useMemo(() => {
    if (!engins) return [];
    const evts: CalendarEvent[] = [];
    engins.forEach((engin, idx) => {
      // Trouve le cycle pour cet engin
      const cycle = cyclesAll?.find((c) => c.engin_id === engin.id);
      const statut = (cycle?.statut ?? STATUT_NON_PLANIFIE) as StatutEngin;
      const ecart = cycle?.ecart_jours ?? 0;

      // Date de l'event = date_prevue (si OK) ou today + 1j (pour En retard/Non planifié)
      let eventDate: Date;
      if (cycle?.date_prevue) {
        eventDate = new Date(cycle.date_prevue);
      } else {
        // Place les events "Non planifié" sur le mois en cours (1 par jour)
        const day = (idx % 28) + 1;
        eventDate = new Date(date.getFullYear(), date.getMonth(), day, 9, 0);
      }
      // Si hors mois en cours, on garde le mois en cours pour la visibilité
      if (eventDate.getMonth() !== date.getMonth() || eventDate.getFullYear() !== date.getFullYear()) {
        const day = (idx % 28) + 1;
        eventDate = new Date(date.getFullYear(), date.getMonth(), day, 9, 0);
      }
      const start = eventDate;
      const end = new Date(eventDate.getTime() + 8 * 60 * 60 * 1000); // +8h

      // Badge + titre selon statut
      const badge = statut === "En retard" ? "⚠️" : statut === "OK" ? "✓" : "·";
      evts.push({
        id: engin.id,
        title: `${badge} ${engin.code} (${ecart}j)`,
        start,
        end,
        resource: {
          engin_id: engin.id,
          statut,
          ecart_jours: ecart,
        },
      });
    });

    return evts;
  }, [engins, date, cyclesAll]);

  const eventStyleGetter = (event: CalendarEvent) => ({
    style: {
      backgroundColor: STATUT_COLORS[event.resource.statut],
      borderRadius: "4px",
      opacity: 0.9,
      color: "white",
      border: "0",
      display: "block",
      fontSize: "0.75rem",
      padding: "2px 4px",
    },
  });

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          {t("agenda.title")}
        </h1>
        <p className="text-muted-foreground">
          {t("agenda.subtitle")} ({engins?.length ?? 0} {t("engins.engin_plural")})
        </p>
      </div>

      {/* Sélection engin + date */}
      <Card>
        <CardHeader>
          <CardTitle>{t("agenda.selection")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs">{t("agenda.machine")}</label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={selectedEnginId ?? ""}
                onChange={(e) =>
                  setSelectedEnginId(Number(e.target.value) || null)
                }
              >
                <option value="">{t("agenda.machine_all")}</option>
                {engins?.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.code}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs">{t("agenda.date_cible")}</label>
              <input
                type="date"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={dateCible}
                onChange={(e) => setDateCible(e.target.value)}
              />
            </div>
          </div>

          {cycleResult && (
            <div className="mt-3 rounded-md border border-border p-3 text-sm">
              <div className="flex items-center gap-2">
                <span
                  className="rounded px-2 py-0.5 text-xs text-white"
                  style={{
                    backgroundColor: STATUT_COLORS[cycleResult.statut],
                  }}
                >
                  {cycleResult.statut}
                </span>
                <span>
                  {t("agenda.gap")} <strong>{cycleResult.ecart_jours} jours</strong>
                </span>
                {cycleResult.derniere_intervention && (
                  <span className="text-xs text-muted-foreground">
                    ({t("agenda.last_intervention")} <code>{cycleResult.derniere_intervention}</code>)
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Légende */}
          <div className="mt-3 flex items-center gap-3 text-xs">
            <span>{ht("Légende :")}</span>
            {Object.entries(STATUT_COLORS).map(([statut, color]) => (
              <span key={statut} className="flex items-center gap-1">
                <span
                  className="inline-block h-3 w-3 rounded"
                  style={{ backgroundColor: color }}
                />
                {statut === "OK" ? t("calendrier.statut_ok") :
                 statut === "En retard" ? t("calendrier.statut_late") :
                 statut === "Non planifié" ? t("calendrier.statut_unplanned") :
                 statut}
              </span>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Calendrier */}
      <Card>
        <CardContent className="p-2">
          <div style={{ height: "600px" }}>
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: "100%" }}
              view={view}
              onView={(v) => setView(v)}
              date={date}
              onNavigate={(d) => setDate(d)}
              onSelectEvent={(event) => {
                const enginId = (event as CalendarEvent).resource.engin_id;
                navigate(`/engins/${enginId}`);
              }}
              views={["month", "week", "day", "agenda"]}
              eventPropGetter={eventStyleGetter}
              messages={{
                today: t("calendrier.today"),
                previous: t("calendrier.previous"),
                next: t("calendrier.next"),
                month: t("calendrier.month"),
                week: t("calendrier.week"),
                day: t("calendrier.day"),
                agenda: t("calendrier.agenda"),
                date: t("calendrier.date"),
                time: t("calendrier.time"),
                event: t("calendrier.event"),
                noEventsInRange: t("calendrier.no_events"),
                showMore: (n: number) => t("calendrier.show_more").replace("{n}", String(n)),
              }}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}